import { useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import logo from "./images/ualogogreen.png";
import CircularProgress from "@mui/material/CircularProgress";

const POST_AUTH_REDIRECT_KEY = "postAuthRedirect";
const DEFAULT_POST_AUTH_ROUTE = "/";

function getSafePostAuthRoute(route) {
	if (!route || typeof route !== "string") {
		return DEFAULT_POST_AUTH_ROUTE;
	}
	if (!route.startsWith("/") || route.startsWith("//")) {
		return DEFAULT_POST_AUTH_ROUTE;
	}
	if (route.startsWith("/SignIn") || route.startsWith("/sso/callback")) {
		return DEFAULT_POST_AUTH_ROUTE;
	}
	return route;
}

export default function SSOCallback() {
	const [searchParams] = useSearchParams();
	const navigate = useNavigate();

	useEffect(() => {
		async function bootstrap() {
			
			const token = searchParams.get("token");
			const ccidRaw = (searchParams.get("ccid") || "").trim().toLowerCase();

			// 1) No token → back to login
			if (!token) {
				return navigate("/SignIn", { replace: true });
			}

			// 2) Store the raw SSO info
			sessionStorage.setItem("token", token);
			sessionStorage.setItem("ccid", ccidRaw);
			sessionStorage.setItem("email", searchParams.get("email") || "");
			sessionStorage.setItem("id", searchParams.get("id") || "");
			sessionStorage.setItem("first_name", searchParams.get("first_name") || "");
			sessionStorage.setItem("last_name", searchParams.get("last_name") || "");

			// 3) Fetch your DRF “userAuthLevel” endpoint
			let access = "";
			let viewableEmpids = "";
			let deptIds = "";
			let userSettings = {};

			try {
				const resp = await fetch(
					`${process.env.REACT_APP_BACKEND_HOST}/spot/userAuthLevel/${ccidRaw}/`,
					{
						headers: { Authorization: `Token ${token}` },
					}
				);
				if (!resp.ok) {
					throw new Error(`HTTP ${resp.status}`);
				}
				const data = await resp.json();
	
				// 4) Admin override or real data
				access = data.access;
				viewableEmpids = data.empids;
				deptIds = data.dept_ids;
				userSettings = data.user_settings || {};
			} catch (err) {
				console.error("Failed to fetch userAuthLevel:", err);
				// fallback values left as empty strings / object
			}

			// 5) Persist everything (even if empty)
			sessionStorage.setItem("access", access);
			sessionStorage.setItem("viewable_empids", viewableEmpids);
			sessionStorage.setItem("dept_ids", deptIds);
			sessionStorage.setItem("user_settings", JSON.stringify(userSettings));

			// 6) Finally—onto your app’s root
			const redirectRoute = getSafePostAuthRoute(
				sessionStorage.getItem(POST_AUTH_REDIRECT_KEY)
			);
			sessionStorage.removeItem(POST_AUTH_REDIRECT_KEY);
			navigate(redirectRoute, { replace: true });
			window.location.reload();
		}

		bootstrap();
	}, [navigate, searchParams]);

	return (
		<div className="bg-white h-screen w-full mx-auto my-auto z-40">
			<div className="flex flex-col items-center  justify-between h-1/2 pt-10">
				<div className="flex flex-col items-center gap-2 mb-5">
					<img src={logo} alt="UofA logo" height={50} width={183} />
					<p className="text-primary font-semibold text-2xl">
						Student Perspectives of Teaching (SPOT)
					</p>
				</div>
				<div className="flex flex-col items-center gap-4">
					<p className="text-2xl font-semibold text-primary">
						Authentication Successful. Please wait while you are redirected.
					</p>
					<CircularProgress size={40} sx={{ color: "primary" }} />
				</div>
			</div>
		</div>
	);
}
